/**
 * L7 (vault 279, PD-56): los montos de Precios Dinámicos en la moneda de la empresa.
 *
 * La moneda es la moneda base de Datos maestros. Mismo patrón que
 * `setGlobalDecimalPlaces` de `format.ts`: la carga la configuración del módulo al
 * entrar, y cualquier formateador (también los de los gráficos, que no son
 * componentes) la lee al momento de dibujar.
 *
 * Sin moneda configurada —una empresa recién creada no tiene ninguna— va sólo el
 * número. Nunca «Bs» ni «$» por defecto: sería mentirle a quien no está en Bolivia.
 *
 * Números con coma decimal y punto de miles («1.234,50»), como el resto de Prixia
 * (`formato-precio.ts`). Un formato por empresa sería de todo Prixia, no del módulo
 * (duda D-L7-3 del plan 279).
 */
export interface MonedaDeEmpresa {
  code: string;
  symbol: string;
}

export const LOCALE_DE_NUMEROS = 'es-BO';

let _moneda: MonedaDeEmpresa | null = null;

export function setMonedaDeLaEmpresa(m: MonedaDeEmpresa | null): void {
  _moneda = m;
}

export function getMonedaDeLaEmpresa(): MonedaDeEmpresa | null {
  return _moneda;
}

const esNumero = (v: number | null | undefined): v is number =>
  typeof v === 'number' && Number.isFinite(v);

/** Sólo el número: «1.234,50». Ausente = «—». */
export function formatAmount(value: number | null | undefined, decimals = 2): string {
  if (!esNumero(value)) return '—';
  return value.toLocaleString(LOCALE_DE_NUMEROS, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

const signoDe = (m: MonedaDeEmpresa) => m.symbol?.trim() || m.code;

/**
 * «S/ 1.234,50». `currency` sin pasar = la moneda vigente de la empresa;
 * `null` = sin moneda. Ausente = «—», nunca 0.
 */
export function formatMoney(
  value: number | null | undefined,
  currency: MonedaDeEmpresa | null | undefined = undefined,
  decimals = 2
): string {
  if (!esNumero(value)) return '—';
  const moneda = currency === undefined ? _moneda : currency;
  const numero = formatAmount(value, decimals);
  return moneda ? `${signoDe(moneda)} ${numero}` : numero;
}

/** El rótulo de una columna o un campo: «Costo (S/)», o «Costo» sin moneda. */
export function conMoneda(texto: string): string {
  return _moneda ? `${texto} (${signoDe(_moneda)})` : texto;
}
